import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, useConfig } from '../../api/api';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import SendIcon from '@mui/icons-material/Send';
import Loader from '../special/Loader';
import MediaPreview from '../exo/explorer/MediaPreview';
import MarqueeText from '../special/MarqueeText';
import FormFields from '../form/FormFields';
import * as Yup from 'yup';
import { BodyHtmlEmailPreviewIframe } from '../email-system/ECEmailConversation';
import { useUserPreferences } from '../../global/useUserPreferences';
import useFetch from '../../api/useFetch';

export const NewEmail = ({
  credentials,
  selectedFolder,
  onNewMail,
  oldEmail,
  type,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { apiData } = useFetch(`text-blocks?type[eq]=signature`);

  const { t } = useTranslation();
  const config = useConfig();
  const { preferences, updatePreferences } = useUserPreferences();

  const [emailOptions, setEmailOptions] = useState(null);
  const [dataCollection, setDataCollection] = useState({});
  const [selectedCredentials, setSelectedCredentials] = useState(null);
  const [editMode, setEditMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedFolder && credentials) calculateOptions();
  }, [selectedFolder, credentials]);

  const divider = `
    <table style="width: 100%">
      <tr>
        <td style="padding: 0px; height: 15px;"></td>
      </tr>
      <tr>
        <td style="padding: 0px; height: 2px; background-color: #bfbfbf!important;"></td>
      </tr>
      <tr>
        <td style="padding: 3px; text-align: center; text-transform: uppercase; color: #bfbfbf!important;">${t(
          'last message'
        )}</td>
      </tr>
      <tr>
        <td style="padding: 0px; height: 2px; background-color: #bfbfbf!important;"></td>
      </tr>
      <tr>
        <td style="padding: 0px; height: 15px;"></td>
      </tr>
    </table>`;

  // actions
  function dataForBlank(email) {
    var selectedCred = null;

    credentials.forEach((cred) => {
      cred.inbox.folders.forEach((folder) => {
        if (folder.id === selectedFolder.id) {
          return (selectedCred = cred);
        }
      });
    });
    setSelectedCredentials(selectedCred);

    return {
      credentialsId: selectedCred.id,
      credentials: selectedCred,
      signature: findSignatureForCredentials(selectedCred),
    };
  }

  function dataForReply(email) {
    setEditMode(false);
    const prevMessages =
      email.htmlBody || email.textBody
        ? divider + email.htmlBody || email.textBody
        : '';

    const cred = findCredentialsViaEmail(email);
    if (cred) setSelectedCredentials(cred);
    return {
      to: [convertEmailToSenderParticipant(email)],
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred?.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      signature: findSignatureForCredentials(cred),
    };
  }
  function dataForReplyAll(email) {
    setEditMode(false);

    const cred = findCredentialsViaEmail(email);
    if (cred) setSelectedCredentials(cred);
    return {
      to: [convertEmailToSenderParticipant(email)],
      cc: convertEmailToCustomParticipantArr(email),
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      signature: findSignatureForCredentials(cred),
    };
  }
  function dataForForwarding(email) {
    const prevMessages =
      email.htmlBody || email.textBody
        ? divider + email.htmlBody || email.textBody
        : '';

    const cred = findCredentialsViaEmail(email);
    if (cred) setSelectedCredentials(cred);
    return {
      subject: `${t('FWD')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      attachments: email.attachments,
      inReplyTo: oldEmail.id,
      after: prevMessages,
      signature: findSignatureForCredentials(cred),
    };
  }
  function convertEmailToSenderParticipant(email) {
    return {
      email: email.from[0],
      participant: email.sender,
      type: email.senderType,
    };
  }
  function convertEmailToCustomParticipantArr(email) {
    var newPartArr = [];
    if (email.cc)
      email.cc.forEach((cc) => {
        newPartArr.push({
          email: cc,
          participant: null,
          type: null,
        });
      });

    return newPartArr;
  }
  function findCredentialsViaEmail(email) {
    const cred = credentials.find(
      (obj) =>
        email.to.includes(obj.imapUsername) ||
        email.from.includes(obj.imapUsername)
    );

    return cred || {};
  }

  function findSignatureForCredentials(cred) {
    var signature = null;
    const signatureMap = preferences?.emailSignatureMap || {};
    var signatureId = signatureMap[cred.imapUsername];

    if (!signatureId && apiData.data[0]) {
      signature = apiData.data[0];
      updatePreferences(
        '',
        {
          emailSignatureMap: {
            ...signatureMap,
            [cred.imapUsername]: apiData.data[0].id,
          },
        },
        true
      );
    }

    if (signatureId && !signature) {
      signature = apiData?.data.find((obj) => obj.id === signatureId);
    }

    return signature;
  }

  const typeMap = {
    blank: dataForBlank,
    reply: dataForReply,
    replyAll: dataForReplyAll,
    forwarding: dataForForwarding,
  };

  useEffect(() => {
    if (credentials && preferences && apiData)
      setDataCollection(typeMap[type || 'blank'](oldEmail));
  }, [oldEmail, credentials, preferences, apiData]);

  // form
  function calculateOptions() {
    var newEmailOptions = [];
    credentials.forEach((cred) => {
      const { color, id, imapUsername, label } = cred;
      newEmailOptions.push({
        label: (
          <Box className="flex items-center">
            <Box sx={{ bgcolor: color }} />
            <Typography fontWeight={600}>{label}</Typography>
            <span className="opacity-50 pl-3">{`<${imapUsername}>`}</span>
          </Box>
        ),
        value: id,
      });
    });
    setEmailOptions(newEmailOptions);
  }

  function sendEmail() {
    const fontUrl = `${config.apiUrl}/fonts/`;
    const defaultStyles = `
    <style>
        @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat.ttf') format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: normal;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat-Italic.ttf')
          format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: italic;
      }
      *{
        margin: 0;
      }
    </style>
    `;
    DBRequest({
      config,
      path: `imap-emails`,
      method: API_POST({
        ...dataCollection,
        htmlBody: `<body>${defaultStyles} ${dataCollection.htmlBody}</body>`,
      }),
      onLoading: setIsLoading,
      onResponse: onNewMail,
    });
  }

  const fields = [
    {
      label: t('from'),
      key: 'credentialsId',
      options: emailOptions,
      type: 'customSelect',
    },
    {
      label: t('to'),
      key: 'to',
      type: 'participantsAutoComplete',
    },
    {
      label: t('cc'),
      key: 'cc',
      type: 'participantsAutoComplete',
    },
    {
      label: t('Attachments'),
      key: 'attachments',
      maxSizeKB: 25600,
      type: 'images',
    },
  ];

  // validation
  const validationSchemaTop = Yup.object().shape({
    credentialsId: Yup.string().required(t('Is required')),
    to: Yup.array()
      .of(
        Yup.object().shape({
          email: Yup.string()
            .email('Invalid email format')
            .required('Email is required'),
        })
      )
      .min(1, 'At least one participant is required')
      .required('Participants array is required'),
  });
  const validationSchemaBottom = Yup.object().shape({
    subject: Yup.string().required(t('Is required')),
    htmlBody: Yup.string().required(t('Is required')),
  });

  const [topIsValid, setTopIsValid] = useState(false);
  const [bottomIsValid, setBottomIsValid] = useState(false);

  return (
    <Box className="flex flex-col lg:flex-row w-full gap-4 lg:h-full">
      <Box className="flex flex-col gap-4 h-full overflow-y-auto w-full">
        <Loader active={isLoading} />
        <Box className="grid grid-cols-2 w-full gap-4 items-start md:flex">
          <Button
            variant="contained"
            color="success"
            disableElevation
            onClick={sendEmail}
            sx={{ height: '46px' }}
            className=" col-span-2"
            disabled={!topIsValid || !bottomIsValid}
          >
            <SendIcon />
          </Button>
          <Box className="w-full col-span-2">
            <Collapse in={editMode}>
              <Box className="flex flex-col gap-4 pt-3 pb-2">
                <FormFields
                  dataCollection={dataCollection}
                  updateDataCollection={(data) => {
                    var cred = {};
                    if (data.credentialsId) {
                      cred = credentials.find(
                        (obj) => obj.id === data.credentialsId
                      );
                    }
                    setDataCollection({ ...dataCollection, ...data, ...cred });
                  }}
                  size="small"
                  fields={fields}
                  validationSchema={validationSchemaTop}
                  onValidationChange={setTopIsValid}
                />
                <Divider />
              </Box>
            </Collapse>

            <Collapse in={!editMode}>
              <Button
                onClick={() => setEditMode(true)}
                sx={{
                  bgcolor: colors.card,
                  display: 'block',
                  textAlign: 'left',
                  border: `2px solid ${
                    topIsValid ? 'transparent' : colors.warning
                  }`,
                  ':hover': {
                    bgcolor: colors.bgInput,
                  },
                }}
                className="w-full rounded-lg p-3"
              >
                <Typography fontSize={12}>
                  <span style={{ fontWeight: 500 }}>{t('from')}:</span>
                  <span className="opacity-50 pl-2">
                    {selectedCredentials?.imapUsername}
                  </span>
                </Typography>
                <Divider />
                <Typography fontSize={12} className="pt-2">
                  <span style={{ fontWeight: 500 }}>{t('to')}:</span>
                  {dataCollection?.to?.map((to, index) => (
                    <span className="opacity-50 pl-2">
                      {to.email}
                      {dataCollection?.to.length !== index + 1 ? ',' : ''}
                    </span>
                  ))}
                </Typography>
                {dataCollection?.cc?.length > 0 && (
                  <Typography fontSize={12}>
                    <span style={{ fontWeight: 500 }}>{t('cc')}:</span>
                    {dataCollection?.cc?.map((cc, index) => (
                      <span className="opacity-50 pl-2">
                        {cc.email}
                        {dataCollection?.cc.length !== index + 1 ? ',' : ''}
                      </span>
                    ))}
                  </Typography>
                )}
                {dataCollection?.attachments && (
                  <Box className="flex items-start w-full gap-2">
                    <Typography className="pr-2">
                      {t('Attachments')}:
                    </Typography>
                    <Box className="flex items-start w-full gap-2 flex-wrap">
                      {dataCollection?.attachments.map((att) => (
                        <Box className="flex gap-1 items-center">
                          <MediaPreview
                            src={att.thumbnailUrl || att.temporaryUrl}
                            size="30px"
                            mimeType={att.mimeType}
                            allowPreview
                          />
                          <MarqueeText text={att.title} width="200px" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Button>
            </Collapse>
          </Box>
        </Box>
        <Box
          className="flex flex-col gap-2 w-full h-full"
          onClick={() => setEditMode(false)}
        >
          <FormFields
            dataCollection={dataCollection}
            startDataCollection={{}}
            updateDataCollection={(data) =>
              setDataCollection({ ...dataCollection, ...data })
            }
            onPreventEnter={() => {}}
            fields={[
              {
                label: t('Subject'),
                key: 'subject',
                type: 'text',
              },
              {
                label: '',
                key: 'htmlBody',
                textBlockType: 'block',
                allowSignature: true,
                signatureKey: 'signature',
                type: 'richTextEditor',
                afterKey: 'after',
                color: '#141414',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
              },
            ]}
            validationSchema={validationSchemaBottom}
            onValidationChange={setBottomIsValid}
          />
        </Box>
      </Box>
      {oldEmail && (
        <Box className="h-full lg:w-[60vw]">
          <Typography fontWeight={500} className="opacity-50">
            {t('Previous messages')}
          </Typography>
          <BodyHtmlEmailPreviewIframe htmlBody={oldEmail.htmlBody} />
        </Box>
      )}
    </Box>
  );
};
